import { memo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { RedesignSignInWithProviderButton } from 'root/src/client/v2/auth/redesign/RedesignSignInWithProviderButton';
import { toast } from 'root/src/client/v2/common/components/ui/sonner';
import { RedesignModalShell } from 'root/src/client/v2/common/components/RedesignModalShell';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { cn } from 'root/src/client/v2/common/helpers/cn';
const PERMISSION_MODE_TABS = [
    { label: 'Automated', auto: true },
    { label: 'Manually', auto: false },
];
// Redesign of the legacy UpgradePermissionsModal: auto/manual tabs, reuses the OAuth button.
// Shown `closeable: false` with an explicit Cancel (the v2 modal context has no on-close hook).
export const RedesignUpgradePermissionsModal = memo((props) => {
    const { autoLabel, manualLabel, autoDescription, manualDescription, scopeSet, provider, popup, header, onContinue, onSuccess, onCancel, } = props;
    const [isAutoModeEnabled, setIsAutoModeEnabled] = useState(true);
    return (<RedesignModalShell className="sm:max-w-md">
			<div className="flex w-full flex-col items-center gap-3 text-center">
				<div className="flex h-12 w-12 items-center justify-center rounded-full border border-solid border-neon-cyan/30 bg-neon-cyan/10">
					<VerifiedUserOutlinedIcon sx={{ fontSize: 24 }} className="text-neon-cyan"/>
				</div>
				<h2 className="font-display text-xl font-bold text-foreground">{header !== null && header !== void 0 ? header : 'We need your permission'}</h2>
			</div>

			<div className="flex min-h-[4rem] w-full items-center justify-center text-center text-sm text-muted-foreground">
				<AnimatePresence mode="wait" initial={false}>
					<motion.div key={isAutoModeEnabled ? 'auto' : 'manual'} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.15 }}>
						{isAutoModeEnabled ? autoDescription : manualDescription}
					</motion.div>
				</AnimatePresence>
			</div>

			<div className="flex min-h-[2.5rem] w-full flex-col items-center gap-2">
				<div role="tablist" aria-label="Permission mode" className="flex w-fit items-center gap-0.5 rounded-lg bg-white/[0.04] p-1">
					{PERMISSION_MODE_TABS.map(({ label, auto }) => {
            const active = isAutoModeEnabled === auto;
            return (<button key={label} type="button" role="tab" aria-selected={active} onClick={() => setIsAutoModeEnabled(auto)} className={cn('relative rounded-lg px-4 py-1.5 text-sm font-medium transition-colors', active ? 'text-white' : 'text-muted-foreground hover:text-foreground')}>
								{active && (<motion.span layoutId="upmModeTab" className="absolute inset-0 rounded-lg bg-primary" transition={{ type: 'spring', bounce: 0.2, duration: 0.4 }}/>)}
								<span className="relative z-10">{label}</span>
							</button>);
        })}
				</div>
				<span className="text-center text-xs text-muted-foreground">{isAutoModeEnabled ? autoLabel : manualLabel}</span>
			</div>

			<div className="flex min-h-[3rem] w-full flex-col items-center justify-center">
				<AnimatePresence mode="wait" initial={false}>
					<motion.div key={isAutoModeEnabled ? 'auto' : 'manual'} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }} className="flex w-full flex-col items-center">
						{isAutoModeEnabled ? (<RedesignSignInWithProviderButton scopeSet={scopeSet} provider={provider} popup={popup} popupOptions={{
                onSuccess: () => {
                    toast.success('Permissions successfully upgraded!');
                    onSuccess();
                },
                onError: (error) => {
                    toast.error((error === null || error === void 0 ? void 0 : error.message) || 'Something went wrong!');
                },
            }}/>) : (<Button variant="brand" onClick={onContinue} className="w-full max-w-xs rounded-lg text-sm font-semibold">Continue</Button>)}
					</motion.div>
				</AnimatePresence>
			</div>

			<Button variant="ghost" size="sm" onClick={onCancel} className="rounded-lg text-sm">Cancel</Button>
		</RedesignModalShell>);
});
